import type { ExistingPatient } from '@/feature/patients/types';
import type { MouseEvent } from 'react';
import { differenceInYears, format, isValid, parseISO } from 'date-fns';
import logoSvg from './assets/full-logo-black.svg?raw';

export const LOGO_DATA_URL = `data:image/svg+xml;utf8,${encodeURIComponent(logoSvg)}`;

export type TemplatePatient = Pick<
  ExistingPatient,
  | 'firstName'
  | 'middleName'
  | 'lastName'
  | 'dateOfBirth'
  | 'genderName'
  | 'address'
  | 'contact'
>;

const GENDER_LABEL: Record<string, string> = {
  male: 'Male',
  female: 'Female',
  na: 'Other',
  other: 'Prefer not to say',
};

export const EMPTY = '—';
export const DASH = '–';
export const NOT_AVAILABLE = 'Not available';

export interface TemplatePractitioner {
  referenceId?: string;
  givenName?: string;
  familyName?: string;
  addressStreet?: string;
  addressCity?: string;
  addressState?: string;
  addressUse?: string;
  roleSpecialty?: string;
  role?: string;
  telecoms: string[];
}

export const formatLongDate = (
  iso?: string | null,
  fallback: string = EMPTY,
): string => {
  if (!iso) return fallback;
  const d = parseISO(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return format(d, 'MMMM d, yyyy');
};

export const formatIsoDate = (
  iso?: string | null,
  fallback: string = EMPTY,
): string => {
  if (!iso) return fallback;
  const trimmed = iso.trim();
  const dateOnly = trimmed.match(/^(\d{4}-\d{2}-\d{2})/)?.[1];
  if (dateOnly) return dateOnly;

  const d = parseISO(trimmed);
  if (Number.isNaN(d.getTime())) return trimmed;
  return format(d, 'yyyy-MM-dd');
};

export const formatDateOfBirthWithAge = (dateOfBirth?: string) => {
  if (!dateOfBirth) return undefined;

  const parsedDateOfBirth = parseISO(dateOfBirth);
  if (!isValid(parsedDateOfBirth)) return dateOfBirth;

  const patientAge = differenceInYears(new Date(), parsedDateOfBirth);
  return `${format(parsedDateOfBirth, 'MM/dd/yyyy')} (${patientAge}y)`;
};

const HTML_TAG_PATTERN = /<\/?[A-Za-z][^>]*>/;
const HTML_ENTITY_PATTERN = /&(?:[A-Za-z][\w]+|#\d+|#x[\dA-Fa-f]+);/;

const cleanTextLines = (value: string) =>
  value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .join('\n');

const parseHtmlTextContent = (value: string) => {
  const document = new DOMParser().parseFromString(value, 'text/html');
  return document.body.textContent ?? value;
};

const addTableCellLineBreaks = (value: string) =>
  value.replace(/<\/t[dh]>/gi, '$&\n').replace(/<br\s*\/?>/gi, '\n');

export const extractMarkupText = (value?: string) => {
  const trimmedValue = value?.trim();
  if (!trimmedValue) return '';

  if (
    typeof DOMParser === 'undefined' ||
    (!HTML_TAG_PATTERN.test(trimmedValue) &&
      !HTML_ENTITY_PATTERN.test(trimmedValue))
  ) {
    return trimmedValue;
  }

  const decodedText = parseHtmlTextContent(trimmedValue);
  if (!HTML_TAG_PATTERN.test(decodedText)) {
    return cleanTextLines(decodedText);
  }

  const parsedCellText = parseHtmlTextContent(
    `<table><tbody><tr>${addTableCellLineBreaks(decodedText)}</tr></tbody></table>`,
  );
  return cleanTextLines(parsedCellText);
};

export const getTimezone = (date: Date = new Date()): string =>
  new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
    .formatToParts(date)
    .find((p) => p.type === 'timeZoneName')?.value ?? '';

export const formatTimestamp = (
  iso?: string | null,
  fallback: string = NOT_AVAILABLE,
) => {
  if (!iso) return fallback;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return format(d, 'MMM d, yyyy, h:mm a');
};

export const formatDateTimeWithTimezone = (
  iso?: string | null,
  fallback: string = EMPTY,
): string => {
  if (!iso) return fallback;
  const d = parseISO(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const timezone = getTimezone(d);
  const formatted = format(d, 'MMMM d, yyyy, h:mm a');
  return timezone ? `${formatted} ${timezone}` : formatted;
};

export const formatShortDate = (
  iso?: string | null,
  fallback: string = DASH,
) => {
  if (!iso) return fallback;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return format(d, 'MMM. d, yyyy');
};

export const scrollToTemplateAnchor = (anchorId: string) => {
  document
    .getElementById(anchorId)
    ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
};

export const handleTemplateAnchorClick = (
  event: MouseEvent<HTMLAnchorElement>,
  anchorId: string,
) => {
  event.preventDefault();
  scrollToTemplateAnchor(anchorId);
};

export const formatPractitionerName = (
  p?: TemplatePractitioner,
  fallback: string = NOT_AVAILABLE,
) => {
  if (!p) return fallback;
  const name = [p.givenName, p.familyName].filter(Boolean).join(' ').trim();
  return name || fallback;
};

export const formatPractitionerNameWithRole = (
  p?: TemplatePractitioner,
  fallback: string = NOT_AVAILABLE,
) => {
  if (!p) return fallback;
  const name = [p.givenName, p.familyName].filter(Boolean).join(' ').trim();
  const withRole = [name, p.role].filter(Boolean).join(', ');
  return withRole || fallback;
};

export const formatPractitionerAddress = (
  p?: TemplatePractitioner,
  fallback: string = NOT_AVAILABLE,
) => {
  if (!p) return fallback;
  const cityState = [p.addressCity, p.addressState].filter(Boolean).join(', ');
  const parts = [p.addressStreet, cityState].filter(Boolean);
  return parts.length ? parts.join(' • ') : fallback;
};

export const formatPractitionerPhone = (
  p?: TemplatePractitioner,
  fallback: string = NOT_AVAILABLE,
) => {
  const phones = (p?.telecoms ?? []).filter(Boolean);
  return phones.length ? phones.join(' • ') : fallback;
};

export const formatRoleSpecialty = (
  p?: TemplatePractitioner,
  fallback: string = NOT_AVAILABLE,
) => {
  if (!p) return fallback;
  const parts = [p.roleSpecialty, p.role].filter(Boolean);
  return parts.length ? parts.join(' • ') : fallback;
};

export const formatFullName = (patient?: TemplatePatient) => {
  if (!patient) return EMPTY;
  const name = [patient.firstName, patient.middleName, patient.lastName]
    .filter(Boolean)
    .join(' ');
  return name || EMPTY;
};

export const formatGender = (gender?: string) => {
  if (!gender) return EMPTY;
  return GENDER_LABEL[gender] ?? gender;
};

export const formatAddress = (address?: TemplatePatient['address']) => {
  if (!address) return EMPTY;
  const cityState = [address.city, address.state].filter(Boolean).join(', ');
  const parts = [
    address.address1,
    address.address2,
    cityState,
    address.postalCode,
  ]
    .map((part) => part?.trim())
    .filter(Boolean);
  return parts.length ? parts.join(' • ') : EMPTY;
};

export const formatPhone = (contact?: TemplatePatient['contact']) => {
  return contact?.phoneNumber?.trim() || EMPTY;
};
